import { Dispatch } from 'react-redux';
import { AuthActionCreators, IApplicationState } from '.';
import { IAdpRecipientFromChain, IBaseResponse, ICenter, ICluster, IListItem, ILogisticsProvider, INetworkEmailSender, IPositionCoordinator, ISignature, IContact } from '../models';
import { ContactEmailFlag } from '../enums/contact-email-flag';

export interface INetworkEmailLookupDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            centerList: ICenter[],
            logisticsProviderList: ILogisticsProvider[],
            emailFlagList: IListItem[],
            centerContactPositionList: IListItem[],
            emailSenders: INetworkEmailSender[],
            staffList: IContact[]
        }
    }
}

export interface INetworkEmailDefaultContentResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            from: string,
            subject: string,
            receiveEmailOption: ContactEmailFlag,
            additionalRecipients: string[],
            swapNumber: string,
            swaps: ICluster[],
            adpRecipientsFromChain: IAdpRecipientFromChain[],
            coordinators: IPositionCoordinator[],
            swapManager: IContact,
            positions: number[],
            clcPositions: number[],
            extra: string[],
            notes: [],
            deadlineDateTime: string,
            deadlineDate: string,
            deadlineTime: string,
            dateTime: string,
            canDoVirtual: boolean,
            selectedCenters: number[],
            signature: ISignature,
            currentUserEmail: string
        }
    }
}

export interface ISendEmailRequest {
    mode: string,
    clusterId: number,
    sender: string,
    subject: string,
    message: string,
    hasMeeting: boolean,
    meetingStartDate: string,
    meetingStartTime: string,
    meetingEndDate: string,
    meetingEndTime: string,
    requireRsvp: boolean,
    courierList: number[],
    centerList: number[],
    receiveEmailOption: number,
    contactPositionList: number[],
    sendToOpo: boolean,
    additionalRecipients: string[],
    selectedRecipients: string[],
    id: number,
    files: File[],
    positions: number[],
    clcPositions: number[],
    extra: string[],
    deadlineDate: string,
    deadlineTime: string,
    dateTime: string,
    canDoVirtual: boolean,
    force: boolean,
    useStreamlinedCancellation: boolean,
    realtimeSwapFailure: boolean,
    preselectReversal: boolean,
    prereversalResponsibles: any[],
    type: string,
    sendImmediately: boolean,
    testRecipientList: string[],
    sendTestOnly: boolean
}

export const ActionCreators = {
    getLookupData: () => async (dispatch: Dispatch<IApplicationState>): Promise<INetworkEmailLookupDataResponse> => {
        var options = {
            url: __API__ + '/email/network/lookup-data'
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getDefaultContent: (mode: string, id: string, positions: string, clcPositions: string, date: string, deadline: string, force: string, type: string, realtimeSwapFailure: number, vendor?: string) => async (dispatch: Dispatch<IApplicationState>): Promise<INetworkEmailDefaultContentResponse> => {
        var options = {
            url: __API__ + '/email/network/default-content',
            data: {
                mode,
                id,
                positions,
                clcPositions,
                date,
                deadline,
                force,
                type,
                realtimeSwapFailure,
                vendor
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    sendEmails: (request: ISendEmailRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var formData = new FormData();
        formData.append('mode', request.mode);
        formData.append('clusterId', request.clusterId.toString());
        formData.append('sender', request.sender);
        formData.append('subject', request.subject);
        formData.append('message', request.message);
        formData.append('hasMeeting', request.hasMeeting.toString());
        formData.append('meetingStartDate', request.meetingStartDate);
        formData.append('meetingStartTime', request.meetingStartTime);
        formData.append('meetingEndDate', request.meetingEndDate);
        formData.append('meetingEndTime', request.meetingEndTime);
        formData.append('requireRsvp', request.requireRsvp.toString());

        request.courierList.forEach(courier => {
            formData.append('courierList[]', courier.toString());
        });

        request.centerList.forEach(center => {
            formData.append('centerList[]', center.toString());
        });

        formData.append('receiveEmailOption', request.receiveEmailOption.toString());

        request.contactPositionList.forEach(position => {
            formData.append('contactPositionList[]', position.toString());
        });

        formData.append('sendToOpo', request.sendToOpo.toString());

        request.additionalRecipients.forEach(additional => {
            formData.append('additionalRecipients[]', additional);
        });

        request.selectedRecipients.forEach(selected => {
            formData.append('selectedRecipients[]', selected);
        });

        formData.append('id', request.id.toString());

        request.files.forEach(file => {
            formData.append('files', file);
        });

        request.positions.forEach(position => {
            formData.append('positions[]', position.toString());
        });

        request.clcPositions.forEach(position => {
            formData.append('clcPositions[]', position.toString());
        });

        request.extra.forEach(extra => {
            formData.append('extra[]', extra);
        });


        formData.append('deadlineDate', request.deadlineDate);
        formData.append('deadlineTime', request.deadlineTime);
        formData.append('dateTime', request.dateTime);

        formData.append('force', request.force.toString());
        formData.append('useStreamlinedCancellation', request.useStreamlinedCancellation.toString());
        formData.append('realtimeSwapFailure', request.realtimeSwapFailure.toString());
        formData.append('preselectReversal', request.preselectReversal.toString());

        request.prereversalResponsibles.forEach((item, index) => {
            formData.append(`prereversalResponsibles[${index}].ClusterId`, item.clusterId);
            formData.append(`prereversalResponsibles[${index}].PositionNumber`, item.positionNumber);
            formData.append(`prereversalResponsibles[${index}].ReversalResponsible`, item.reversalResponsible);
        });

        formData.append('type', request.type);

        request.testRecipientList.forEach(email => {
            formData.append('testRecipientList[]', email);
        });

        formData.append('sendTestOnly', request.sendTestOnly.toString());

        var options = {
            url: __API__ + '/email/network/send-email',
            data: formData
        };

        return await dispatch(AuthActionCreators.apiPostFormData(options));
    },
}

export interface INetworkEmailState {
}

var initialState: INetworkEmailState = {

};

const createReducer = (initialState: INetworkEmailState, handlers: any) => {
    return function reducer(state = initialState, action: any) {
        if (handlers.hasOwnProperty(action.type)) {
            let s = Object.assign({}, state);
            return handlers[action.type](s, action);
        } else {
            return state;
        }
    }
}

const handlers = {
};

export const Reducer = createReducer(initialState, handlers);
